import React, { useRef, useCallback, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./PhysicalModel1.css";

/* === Toolbar (separate component) === */
function Toolbar({ onImport, onExport, onReset, onDownload, downloading }) {
  return (
    <div className="toolbar">
      <button onClick={onImport}>💾 Import your diagram</button>
      <button onClick={onExport}>💾 Export your diagram</button>
    </div>
  );
}

/* === Functional View === */
export default function FunctionalView1() {
  const iframeRef = useRef(null);
  const history = useHistory();
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState("");

  // 🔄 Reset diagram
  const handleReset = () => {
    if (iframeRef.current) iframeRef.current.src = iframeRef.current.src;
  };

  // 📥 Import alert
  const handleImport = () => {
    alert("📂 To import a diagram, use 'File > Open Project' inside Visual Paradigm.");
  };

  // 💾 Export alert
  const handleExport = () => {
    alert("💾 To export your diagram, use 'File > Export > PDF/PNG' inside Visual Paradigm.");
  };

  // 📄 Capture PDF
  const handleDownloadPDF = useCallback(async () => {
    try {
      const element = document.querySelector(".vp-container");
      if (!element) {
        alert("❌ Unable to find the area to capture!");
        return;
      }

      setDownloading(true);
      window.scrollTo(0, 0);
      document.body.classList.add("no-animations");

      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = await html2canvas(element, {
        scale: 3,
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: true,
      });

      document.body.classList.remove("no-animations");

      // 🎨 Contrast enhancement
      const ctx = canvas.getContext("2d");
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(data[i] * 1.05 + 8, 255);
        data[i + 1] = Math.min(data[i + 1] * 1.05 + 8, 255);
        data[i + 2] = Math.min(data[i + 2] * 1.05 + 8, 255);
      }
      ctx.putImageData(imageData, 0, 0);

      // 🧾 Create PDF
      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = pdfWidth / imgWidth;
      const newHeight = imgHeight * ratio;

      let position = 0;
      while (position < newHeight) {
        pdf.addImage(imgData, "PNG", 0, -position, pdfWidth, newHeight);
        position += pdfHeight;
        if (position < newHeight) pdf.addPage();
      }

      const fileName = `Functional-view-activity-${new Date()
        .toISOString()
        .split("T")[0]}.pdf`;
      pdf.save(fileName);

      setMessage("✅ Full capture saved in your Downloads folder!");
      setDownloading(false);

      // 🔁 Auto-redirect
      setTimeout(() => {
        history.push("/process-view-1");
      }, 1500);

    } catch (error) {
      console.error("🚨 Capture error:", error);
      alert("Error during PDF generation: " + (error.message || error));
      setDownloading(false);
    }
  }, [history]);

  return (
    <>
      <div className="vp-wrapper">
        {/* Introduction */}
        <div className="functional-intro">
          <h2>⚙️ Functional View – Activity Diagram</h2>
          <p>
            The <strong>functional view</strong> illustrates the main activities and
            internal processes of the system. It highlights the sequence of actions,
            decisions, and logic that define the key operations of the project.
          </p>
        </div>

        {/* Toolbar */}
        <Toolbar
          onImport={handleImport}
          onExport={handleExport}
          onReset={handleReset}
          onDownload={handleDownloadPDF}
          downloading={downloading}
        />

        {/* Visual Paradigm – Activity Diagram */}
        <div className="vp-container">
          <iframe
            ref={iframeRef}
            title="Visual Paradigm – Functional View"
            className="vp-editor-frame"
            src="https://online.visual-paradigm.com/app/diagrams/#diagram:proj=0&type=ActivityDiagram&width=11&height=8.5&unit=inch"
            allowFullScreen
          />
        </div>
      </div>

      {/* Status message */}
      {message && (
        <p
          style={{
            textAlign: "center",
            color: message.startsWith("✅") ? "green" : "black",
            marginTop: "10px",
            fontWeight: 500,
          }}
        >
          {message}
        </p>
      )}

      {/* Navigation */}
      <div className="icam-navigation">
        <Link to="/process-view-1">
          <button className="icam-nav-btn">Back</button>
        </Link>
        <Link to="/decisional-model-1">
          <button className="icam-nav-btn">Continue</button>
        </Link>
      </div>
    </>
  );
}
